import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Plus, MessageSquare, ChevronRight, Loader2, LifeBuoy, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import SEOHead from '@/components/SEOHead';
import { useAuth } from '@/context/AuthContext';
import { extractErrorMessage } from '@/lib/errorUtils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_META = {
  open:          { label: 'Open',        className: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  in_progress:   { label: 'In progress', className: 'bg-amber-100 text-amber-700 border-amber-200' },
  resolved:      { label: 'Resolved',    className: 'bg-blue-100 text-blue-700 border-blue-200' },
  closed:        { label: 'Closed',      className: 'bg-slate-200 text-slate-600 border-slate-300' },
};

const CATEGORY_LABELS = {
  bug: 'Bug',
  billing: 'Billing',
  feature_request: 'Feature request',
  account: 'Account',
  other: 'Other',
};

const fmtDate = (iso) => {
  try { return new Date(iso).toLocaleString(); } catch { return iso || ''; }
};

/** Landing page for the user-facing support flow — list of my tickets. */
export default function MySupportTickets() {
  const { isAuthenticated, isLoading } = useAuth();
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Wait for AuthContext to finish restoring the session before deciding
    // whether to redirect — otherwise a refresh of /support kicks the user
    // to /login before `isAuthenticated` has been hydrated from storage.
    if (isLoading) return;
    if (!isAuthenticated) { navigate('/login'); return; }
    (async () => {
      try {
        const token = localStorage.getItem('session_token');
        const res = await fetch(`${API_URL}/api/support/tickets`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          toast.error(extractErrorMessage(data, 'Could not load tickets'));
          return;
        }
        setTickets(data.tickets || []);
      } finally {
        setLoading(false);
      }
    })();
  }, [isAuthenticated, isLoading, navigate]);

  return (
    <div className="min-h-screen bg-slate-50">
      <SEOHead title="Support | Schedule Guru" description="Contact the Schedule Guru support team." path="/support" />
      <div className="max-w-3xl mx-auto px-4 py-8">
        <div className="mb-4">
          <Link to="/my-schedules">
            <Button variant="ghost" size="sm" data-testid="support-back-to-dashboard-btn">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to dashboard
            </Button>
          </Link>
        </div>
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2 text-[#00247D]">
            <LifeBuoy className="w-5 h-5" />
            <h1 className="font-bold tracking-wide text-xl" style={{ fontFamily: 'Barlow Condensed' }}>
              MY SUPPORT TICKETS
            </h1>
          </div>
          <Link to="/support/new">
            <Button className="bg-[#00247D] hover:bg-[#001A5C] text-white" data-testid="new-ticket-btn">
              <Plus className="w-4 h-4 mr-1" /> New ticket
            </Button>
          </Link>
        </div>

        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
        ) : tickets.length === 0 ? (
          <Card>
            <CardContent className="py-14 text-center">
              <MessageSquare className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-600 font-medium">No tickets yet</p>
              <p className="text-slate-400 text-sm mt-1 mb-5">
                Hit a bug or have a question we didn't cover in the manual?
              </p>
              <Link to="/support/new">
                <Button className="bg-[#00247D] hover:bg-[#001A5C] text-white" data-testid="empty-new-ticket-btn">
                  Open your first ticket
                </Button>
              </Link>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-2" data-testid="ticket-list">
            {tickets.map((t) => {
              const meta = STATUS_META[t.status] || STATUS_META.open;
              return (
                <Link key={t.id} to={`/support/${t.id}`} data-testid={`ticket-row-${t.id}`}>
                  <Card className="hover:border-[#00247D]/40 hover:shadow-sm transition-all cursor-pointer">
                    <CardContent className="p-4 flex items-center gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <Badge variant="outline" className={`${meta.className} text-[11px]`}>{meta.label}</Badge>
                          <span className="text-[11px] text-slate-500">
                            {CATEGORY_LABELS[t.category] || t.category}
                          </span>
                        </div>
                        <div className="font-semibold text-slate-900 truncate">{t.subject}</div>
                        <div className="text-xs text-slate-500 mt-0.5">
                          Updated {fmtDate(t.updated_at)}
                          {t.last_reply_by === 'admin' && (
                            <span className="ml-2 text-emerald-600 font-semibold">· New reply</span>
                          )}
                        </div>
                      </div>
                      <ChevronRight className="w-5 h-5 text-slate-300 flex-shrink-0" />
                    </CardContent>
                  </Card>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
